'use client';

import Link from 'next/link';
import { useQuery } from '@tanstack/react-query';
import { api } from '@/shared/services/api';
import { useSubscriptionStatus } from '@/features/billing/hooks/use-billing';
import { useHasModule } from '@/features/billing/hooks/use-has-module';
import { WidgetTeaser } from '@/features/billing/components/widget-teaser';
import { usePermissions } from '@/features/auth/hooks/use-permissions';
import { useMemo, useState } from 'react';
import {
  TrendingDown, FileText,
  Send, Receipt, Shield, ArrowUpRight,
  FileEdit, Truck, UserPlus, Landmark, BarChart3, Repeat, Package, Coins,
} from 'lucide-react';
import {
  useDashboardStats,
  useRevenueChart,
  useRecentInvoices,
  useOverdueInvoices,
  useOverdueTrend,
  useCollectionRate,
  useVatSummary,
} from '../hooks/use-dashboard';
import { useInvoiceStats } from '@/features/invoices/hooks/use-invoices';
import { EmptyChart, EmptyList, EMPTY_COPY, WidgetErrorState } from './empty-states';
import { formatNumber } from '@/shared/utils/number-format';
import { splitAmount } from './kpi-format';

function fmtShort(n: number, currency = 'RON'): string {
  const num = formatNumber(Math.round(n), 0);
  return num + ' ' + currency;
}

// Backend-ul (getRevenueChart) trimite luna LOCALIZATĂ ro-RO: „aug. 2025",
// „sept. 2025", „mai 2026". Vechiul `.slice(5, 7)` presupunea „YYYY-MM" și
// producea etichete corupte („20", „2", „02"). Tolerant la ambele formate.
function monthShort(m: string): string {
  if (/^\d{4}-\d{2}/.test(m)) return m.slice(5, 7);
  return (m.split(' ')[0] || m).replace(/\.$/, '');
}

function daysBetween(from: Date, to: Date): number {
  return Math.floor((to.getTime() - from.getTime()) / 86400000);
}

/* ───────────── KPI Group ───────────── */

export function RevenueMonthWidget() {
  const { data: stats, isLoading, isError, refetch } = useDashboardStats();
  if (isLoading) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const rev = Number(stats?.revenueThisMonth || 0);
  const parts = splitAmount(rev);
  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] text-neutral-900 dark:text-neutral-100 my-2"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontWeight: 600, fontSize: '2.125rem' }}
      >
        {parts.main}
        {parts.decimals && (
          <span className="font-normal text-neutral-500 dark:text-neutral-400" style={{ fontSize: '1.125rem' }}>{parts.decimals}</span>
        )}
        <span className="ml-2 text-[0.75rem] font-medium text-neutral-400 dark:text-neutral-500">RON</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        {stats?.invoicesIssued || 0} facturi emise · {stats?.clientsActive || 0} clienți activi
      </div>
    </>
  );
}

/**
 * "Facturi neîncasate" — TOATE facturile neplătite (in termen + depășite).
 * Sursă: /invoices/stats → unpaidTotal + unpaidCount.
 */
export function OutstandingWidget() {
  const { data: stats, isLoading, isError, refetch } = useInvoiceStats();
  if (isLoading) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const total = Number(stats?.unpaidTotal || 0);
  const count = Number(stats?.unpaidCount || 0);
  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] my-2"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontWeight: 600, fontSize: '2.125rem', color: total > 0 ? 'rgb(60, 60, 60)' : 'rgb(15,14,15)' }}
      >
        {splitAmount(total).main}
        <span className="font-normal text-neutral-500 dark:text-neutral-400" style={{ fontSize: '1.125rem' }}>{splitAmount(total).decimals}</span>
        <span className="ml-2 text-[0.75rem] font-medium text-neutral-400 dark:text-neutral-500">RON</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        <span className={count > 0 ? 'font-semibold text-neutral-700 dark:text-neutral-300' : ''}>{count}</span> {count === 1 ? 'factură neîncasată' : 'facturi neîncasate'}
      </div>
    </>
  );
}

/**
 * "Facturi depășite" — subset al neîncasatelor: cele cu scadența depășită (overdue).
 * Sursă: /invoices/stats → overdueTotal + overdueCount.
 */
export function OverdueKpiWidget() {
  const { data: stats, isLoading, isError, refetch } = useInvoiceStats();
  if (isLoading) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const total = Number(stats?.overdueTotal || 0);
  const count = Number(stats?.overdueCount || 0);
  const hasOverdue = total > 0;
  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] my-2"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontWeight: 600, fontSize: '2.125rem', color: hasOverdue ? 'rgb(185, 28, 28)' : 'rgb(15,14,15)' }}
      >
        {splitAmount(total).main}
        <span className="font-normal text-neutral-500 dark:text-neutral-400" style={{ fontSize: '1.125rem' }}>{splitAmount(total).decimals}</span>
        <span className="ml-2 text-[0.75rem] font-medium text-neutral-400 dark:text-neutral-500">RON</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        <span className={hasOverdue ? 'text-red-600 dark:text-red-400 font-semibold' : ''}>{count}</span> {count === 1 ? 'factură depășită' : 'facturi depășite'}
      </div>
    </>
  );
}

/**
 * DSO (Days Sales Outstanding) — formula standardă:
 *   DSO = (Sold neîncasat / Vânzări lună până azi) × ziua curentă din lună
 *
 * Reprezintă: câte zile de vânzări sunt "blocate" în facturi neîncasate.
 * Cifră MARE = încasare lentă; cifră MICĂ = încasare rapidă.
 */
export function DsoWidget() {
  const { data: invoiceStats, isLoading: l1, isError: e1, refetch: r1 } = useInvoiceStats();
  const { data: dashStats, isLoading: l2, isError: e2, refetch: r2 } = useDashboardStats();
  if (l1 || l2) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (e1 || e2) return <WidgetErrorState onRetry={() => { r1(); r2(); }} />;

  const unpaid = Number(invoiceStats?.unpaidTotal || 0);
  const revenueMtd = Number(dashStats?.revenueThisMonth || 0);
  const dayOfMonth = new Date().getDate();

  // Avoid division by zero when no revenue this month
  const dso = revenueMtd > 0 ? (unpaid / revenueMtd) * dayOfMonth : 0;
  const hasData = revenueMtd > 0 || unpaid > 0;

  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] text-neutral-900 dark:text-neutral-100 my-2"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontWeight: 600, fontSize: '2.125rem' }}
      >
        {hasData ? dso.toFixed(0) : '—'}
        <span className="ml-2 text-[0.75rem] font-medium text-neutral-400 dark:text-neutral-500">zile</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        Zile de vânzări blocate în facturi neîncasate
      </div>
    </>
  );
}

/**
 * Cash Position — flux de numerar net pentru luna curentă.
 *   = Încasat (de la clienți) − Plătit (către furnizori)
 *
 * Pozitiv = ai mai mult cash decât la începutul lunii;
 * Negativ = ai cheltuit mai mult decât ai încasat.
 *
 * Note: aceasta NU e soldul bancar absolut (acela necesită balanță bancară zilnică).
 * E un indicator al "ce s-a mișcat" în luna curentă.
 */
export function CashPositionWidget() {
  const start = startOfMonth();
  const dateFrom = isoDate(start);
  const inflowsQ = useQuery({
    queryKey: ['dashboard', 'cash-inflows', dateFrom],
    queryFn: () =>
      api.get<any>('/payments?' + new URLSearchParams({ limit: '500', dateFrom }).toString())
        .catch(() => ({ data: [] })),
    staleTime: 2 * 60 * 1000,
  });
  // Outflows nu sunt încă tracked separat — placeholder pentru când se va implementa
  // un endpoint dedicat (/payments/supplier sau /expenses).
  const outflowsTotal = 0;

  if (inflowsQ.isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (inflowsQ.isError) return <WidgetErrorState onRetry={() => inflowsQ.refetch()} />;
  const raw = Array.isArray(inflowsQ.data) ? inflowsQ.data : (inflowsQ.data?.data || []);
  const monthStart = start;
  const inflows = (raw as any[]).filter((p) => {
    const d = new Date(p.date || p.paymentDate || p.createdAt);
    return d >= monthStart;
  });
  const inflowTotal = inflows.reduce((s, p) => s + Number(p.amount || 0), 0);
  const netCash = inflowTotal - outflowsTotal;

  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] my-2"
        style={{
          fontFamily: 'var(--font-poppins), sans-serif',
          fontSize: '2rem',
          color: netCash > 0 ? 'rgb(15,14,15)' : netCash < 0 ? 'rgb(185, 28, 28)' : 'rgb(60, 60, 60)',
        }}
      >
        {splitAmount(netCash).main}
        <span className="font-normal text-neutral-500 dark:text-neutral-400" style={{ fontSize: '1rem' }}>{splitAmount(netCash).decimals}</span>
        <span className="ml-2 text-[0.75rem] font-medium text-neutral-400 dark:text-neutral-500">RON</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        Total încasări lună
        <span className="block text-[0.6563rem] mt-0.5 text-neutral-400 dark:text-neutral-500" style={{ fontFamily: 'var(--font-poppins), sans-serif' }}>
          cheltuielile vor fi adăugate ulterior
        </span>
      </div>
    </>
  );
}

/* ───────────── Overdue group ───────────── */

export function AgingWidget() {
  const { data: overdue, isLoading, isError, refetch } = useOverdueInvoices();
  const buckets = useMemo(() => {
    const b = { '0-30': 0, '31-60': 0, '61-90': 0, '90+': 0 };
    if (!overdue) return b;
    const now = new Date();
    for (const inv of overdue as any[]) {
      if (!inv.dueDate) continue;
      const due = new Date(inv.dueDate);
      const d = daysBetween(due, now);
      if (d <= 0) continue;
      const amt = Number(inv.total || 0) - Number(inv.paidAmount || 0);
      if (d <= 30) b['0-30'] += amt;
      else if (d <= 60) b['31-60'] += amt;
      else if (d <= 90) b['61-90'] += amt;
      else b['90+'] += amt;
    }
    return b;
  }, [overdue]);

  const total = Object.values(buckets).reduce((a, b) => a + b, 0);
  if (isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;

  const segs = [
    { key: '0-30', cls: 'bg-emerald-500', val: buckets['0-30'], color: 'text-emerald-600' },
    { key: '31-60', cls: 'bg-amber-500', val: buckets['31-60'], color: 'text-amber-700' },
    { key: '61-90', cls: 'bg-orange-500', val: buckets['61-90'], color: 'text-orange-700' },
    { key: '90+', cls: 'bg-red-500', val: buckets['90+'], color: 'text-red-700' },
  ];

  return (
    <>
      <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-1">
        Total restant: <b className="text-neutral-900 dark:text-neutral-100">{fmtShort(total)}</b>
      </div>
      <div className="flex h-6 rounded-md overflow-hidden my-3 bg-neutral-100 dark:bg-neutral-800">
        {segs.map((s) => (
          <div key={s.key} className={s.cls + ' transition-all hover:scale-y-110'} style={{ flex: s.val || 0.01 }} title={s.key + ' zile: ' + fmtShort(s.val)} />
        ))}
      </div>
      <div className="grid grid-cols-4 gap-1.5">
        {segs.map((s) => (
          <div key={s.key} className="bg-neutral-50 dark:bg-neutral-800/50 rounded-md p-1.5">
            <div className="text-[0.5938rem] uppercase tracking-wider text-neutral-500 dark:text-neutral-400 font-semibold">{s.key}z</div>
            <div className={'text-[0.7813rem] font-semibold ' + s.color}>{fmtShort(s.val).replace(' RON', '')}</div>
          </div>
        ))}
      </div>
    </>
  );
}

export function TopDebitorsWidget() {
  const { data: overdue, isLoading, isError, refetch } = useOverdueInvoices();
  const top = useMemo(() => {
    if (!overdue) return [];
    const map: Record<string, any> = {};
    const now = new Date();
    for (const inv of overdue as any[]) {
      const clientId = inv.clientId;
      if (!clientId) continue;
      if (!map[clientId]) {
        map[clientId] = { clientId, name: inv.client?.name || 'Client', count: 0, total: 0, maxDays: 0 };
      }
      map[clientId].count += 1;
      map[clientId].total += Number(inv.total || 0) - Number(inv.paidAmount || 0);
      const d = inv.dueDate ? daysBetween(new Date(inv.dueDate), now) : 0;
      if (d > map[clientId].maxDays) map[clientId].maxDays = d;
    }
    return Object.values(map).sort((a: any, b: any) => b.total - a.total).slice(0, 4);
  }, [overdue]);

  if (isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  if (top.length === 0) {
    return (
      <EmptyList
        title="Top debitori"
        hint={EMPTY_COPY.topDebitors.hint}
        sub="Toate soldurile clienților sunt încasate."
        tone="positive"
      />
    );
  }

  return (
    <div className="flex flex-col">
      {top.map((c: any) => (
        <div key={c.clientId} className="flex items-center gap-2.5 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0 text-xs">
          <div className="flex-1 min-w-0">
            <div className="font-medium text-neutral-900 dark:text-neutral-100 truncate">{c.name}</div>
            <div className="text-[0.6563rem] text-neutral-500 dark:text-neutral-400">{c.count} {c.count === 1 ? 'factură' : 'facturi'} · max {c.maxDays}z</div>
          </div>
          <span className="font-semibold text-red-600 dark:text-red-400">{fmtShort(c.total).replace(' RON', '')}</span>
        </div>
      ))}
    </div>
  );
}

export function OverdueInvoicesWidget() {
  const { data: overdue, isLoading, isError, refetch } = useOverdueInvoices();
  if (isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const items = (overdue || []).slice(0, 5);
  if (items.length === 0) {
    return (
      <EmptyList
        title="Facturi restante"
        hint={EMPTY_COPY.overdueInvoices.hint}
        sub="Toate facturile emise sunt încasate la timp."
        tone="positive"
      />
    );
  }
  return (
    <div className="flex flex-col">
      {items.map((inv: any) => {
        const due = inv.dueDate ? new Date(inv.dueDate) : null;
        const days = due ? daysBetween(due, new Date()) : 0;
        return (
          <Link
            key={inv.id}
            href={'/facturi?view=' + inv.id}
            className="flex items-center gap-2 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-800/40 -mx-2 px-2 rounded"
          >
            <div className="flex-1 min-w-0">
              <div className="font-medium truncate">{inv.client?.name || '—'}</div>
              <div className="text-[0.6563rem] text-neutral-500">{inv.series}{String(inv.number).padStart(5, '0')} · {days}z întârziere</div>
            </div>
            <span className="font-semibold text-red-600 dark:text-red-400">{fmtShort(Number(inv.total || 0) - Number(inv.paidAmount || 0)).replace(' RON', '')}</span>
          </Link>
        );
      })}
    </div>
  );
}

/* ───────────── Sales / Trends ───────────── */

export function RevenueChartWidget() {
  const { data: chart, isLoading, isError, refetch } = useRevenueChart(12);
  if (isLoading) return <div className="animate-pulse h-40 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const items = (chart || []) as any[];
  const max = Math.max(1, ...items.map((i) => Number(i.revenue) || 0));
  // Cont fără nicio factură în fereastra de 12 luni → empty state de grafic
  // (înainte: dreptunghi alb — barele zero + etichete corupte păreau widget stricat).
  if (items.length === 0 || items.every((i) => !(Number(i.revenue) > 0))) {
    return (
      <EmptyChart
        title="Facturat lunar"
        hint={EMPTY_COPY.revenueChart.hint}
        ctaLabel={EMPTY_COPY.revenueChart.cta}
        ctaHref={EMPTY_COPY.revenueChart.href}
        height={200}
      />
    );
  }
  return (
    <>
      {/* Coloanele au înălțime DEFINITĂ (items-stretch pe containerul h-[200px]),
          altfel `height: N%` al barei se rezolvă la 0 și graficul apare GOL —
          bug istoric: items-end anula stretch-ul → nicio bară vizibilă, nicăieri. */}
      <div className="flex items-stretch gap-1.5 h-[200px] pt-2">
        {items.map((i, idx) => {
          const h = (Number(i.revenue) || 0) / max * 100;
          const isCurrent = idx === items.length - 1;
          return (
            <div key={idx} className="flex-1 min-w-0 flex flex-col items-center gap-1">
              <div className="w-full flex-1 flex items-end">
                <div
                  className={'w-full rounded-t-[4px] transition-all ' + (isCurrent ? 'bg-gradient-to-b from-primary to-primary-700' : 'bg-neutral-300 dark:bg-neutral-700')}
                  style={{ height: h + '%', minHeight: 3 }}
                  title={i.month + ': ' + fmtShort(Number(i.revenue))}
                />
              </div>
              <div className={'shrink-0 text-[0.5938rem] ' + (isCurrent ? 'text-primary font-bold' : 'text-neutral-500')}>
                {monthShort(i.month || '')}
              </div>
            </div>
          );
        })}
      </div>
    </>
  );
}

export function TopClientsWidget() {
  const { data: bigResult, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'top-clients-source'],
    queryFn: () => api.get<any>('/invoices?limit=300&direction=outgoing&type=factura&sort=createdAt&order=desc'),
    staleTime: 5 * 60 * 1000,
  });
  const top = useMemo(() => {
    const list = (bigResult?.data || []) as any[];
    const map: Record<string, any> = {};
    for (const inv of list) {
      const id = inv.client?.id;
      if (!id) continue;
      if (inv.status === 'cancelled' || inv.status === 'draft') continue;
      if (!map[id]) map[id] = { id, name: inv.client.name, count: 0, total: 0 };
      map[id].count += 1;
      map[id].total += Number(inv.total || 0);
    }
    return Object.values(map).sort((a: any, b: any) => b.total - a.total).slice(0, 10);
  }, [bigResult]);

  if (isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  if (top.length === 0) {
    return (
      <EmptyList
        title="Top clienți"
        hint={EMPTY_COPY.topClients.hint}
        ctaLabel={EMPTY_COPY.topClients.cta}
        ctaHref={EMPTY_COPY.topClients.href}
      />
    );
  }

  return (
    <div className="flex flex-col">
      {top.map((c: any, idx: number) => (
        <div key={c.id} className="flex items-center gap-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-800/40 -mx-2 px-2 rounded">
          <span
            className="text-neutral-400 dark:text-neutral-500 italic font-normal w-5 shrink-0"
            style={{ fontStyle: 'italic', fontWeight: 500, fontSize: '1rem' }}
          >
            {(idx + 1).toString()}
          </span>
          <div className="flex-1 min-w-0">
            <div className="font-medium truncate">{c.name}</div>
            <div className="text-[0.6563rem] text-neutral-500">{c.count} {c.count === 1 ? 'factură' : 'facturi'}</div>
          </div>
          <span className="font-semibold">{fmtShort(c.total).replace(' RON', '')}</span>
        </div>
      ))}
    </div>
  );
}

// Badge color + label per document type — pentru distincție vizuală în liste mixte
const DOC_TYPE_BADGE: Record<string, { label: string; cls: string }> = {
  factura: { label: 'F', cls: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400' },
  proforma: { label: 'PF', cls: 'bg-violet-100 text-violet-700 dark:bg-violet-900/30 dark:text-violet-400' },
  aviz: { label: 'AV', cls: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-400' },
  storno: { label: 'ST', cls: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400' },
};

function docTypeRoute(inv: any): string {
  const t = inv.type;
  if (t === 'proforma') return '/proforme?view=' + inv.id;
  if (t === 'aviz') return '/avize?view=' + inv.id;
  return '/facturi?view=' + inv.id;
}

export function RecentInvoicesWidget() {
  const { data: recent, isLoading, isError, refetch } = useRecentInvoices();
  if (isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const items = (recent || []).slice(0, 5);
  if (items.length === 0) {
    return (
      <EmptyList
        title="Documente recente"
        hint={EMPTY_COPY.recentInvoices.hint}
        ctaLabel={EMPTY_COPY.recentInvoices.cta}
        ctaHref={EMPTY_COPY.recentInvoices.href}
      />
    );
  }
  return (
    <div className="flex flex-col">
      {items.map((inv: any) => {
        const badge = DOC_TYPE_BADGE[inv.type] || DOC_TYPE_BADGE.factura!;
        return (
          <Link
            key={inv.id}
            href={docTypeRoute(inv)}
            className="flex items-center gap-2 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0 text-xs hover:bg-neutral-50 dark:hover:bg-neutral-800/40 -mx-2 px-2 rounded"
          >
            <span className={'inline-flex items-center justify-center min-w-[24px] h-[18px] px-1 rounded text-[0.5938rem] font-bold uppercase tracking-wide ' + badge.cls} title={inv.type}>
              {badge.label}
            </span>
            <div className="flex-1 min-w-0">
              <div className="font-semibold">{inv.series}{String(inv.number).padStart(5, '0')}</div>
              <div className="text-[0.6563rem] text-neutral-500 truncate">{inv.client?.name || '—'}</div>
            </div>
            <span className="font-semibold">{fmtShort(Number(inv.total || 0)).replace(' RON', '')}</span>
          </Link>
        );
      })}
    </div>
  );
}

/* ───────────── ANAF ───────────── */

export function AnafStatusWidget() {
  const { data: stats, isLoading, isError, refetch } = useQuery({
    queryKey: ['efactura', 'stats'],
    queryFn: () => api.get<{ sent: number; validated: number; rejected: number; pendingOverdue: number; receivedNew: number }>('/efactura/stats'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: deadlines, isError: e2, refetch: r2 } = useQuery({
    queryKey: ['efactura', 'deadlines'],
    queryFn: () => api.get<Array<{ invoiceId: string; series: string; number: number; workingDaysRemaining: number }>>('/efactura/deadlines'),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <div className="animate-pulse h-28 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError || e2) return <WidgetErrorState onRetry={() => { refetch(); r2(); }} />;

  const urgent = (deadlines || []).filter((d) => d.workingDaysRemaining <= 5).length;
  const closest = (deadlines || [])[0];

  return (
    <>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div className={urgent > 0 ? 'bg-orange-50 dark:bg-orange-950/30 rounded-lg p-2.5' : 'bg-neutral-50 dark:bg-neutral-800 rounded-lg p-2.5'}>
          <div className={'font-semibold text-[1.375rem] leading-none ' + (urgent > 0 ? 'text-primary' : '')} style={{ fontFamily: 'var(--font-poppins), sans-serif' }}>
            {urgent}
          </div>
          <div className="text-[0.5938rem] uppercase tracking-wider text-neutral-500 font-semibold mt-1">urgent</div>
        </div>
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-2.5">
          <div className="font-semibold text-[1.375rem] leading-none" style={{ fontFamily: 'var(--font-poppins), sans-serif' }}>
            {stats?.sent ?? 0}
          </div>
          <div className="text-[0.5938rem] uppercase tracking-wider text-neutral-500 font-semibold mt-1">pending</div>
        </div>
        <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-lg p-2.5">
          <div className="font-semibold text-[1.375rem] leading-none text-emerald-700 dark:text-emerald-400" style={{ fontFamily: 'var(--font-poppins), sans-serif' }}>
            {stats?.validated ?? 0}
          </div>
          <div className="text-[0.5938rem] uppercase tracking-wider text-neutral-500 font-semibold mt-1">validate</div>
        </div>
      </div>
      {closest && urgent > 0 && (
        <div className="mt-3 pt-3 border-t border-neutral-200 dark:border-neutral-800 text-[0.7188rem] text-neutral-600 dark:text-neutral-400">
          Termen urgent: <b className="text-primary">{closest.series}{String(closest.number).padStart(5, '0')}</b> · {closest.workingDaysRemaining} {closest.workingDaysRemaining === 1 ? 'zi' : 'zile'} lucrătoare
        </div>
      )}
      {(stats?.rejected || 0) > 0 && (
        <div className="mt-2 text-[0.7188rem] text-red-600 dark:text-red-400">
          <b>{stats?.rejected}</b> {stats?.rejected === 1 ? 'factură respinsă' : 'facturi respinse'} de ANAF
        </div>
      )}
      <Link href="/efactura" className="text-[0.6875rem] text-primary mt-3 inline-flex items-center gap-1 hover:underline">
        Vezi detalii <ArrowUpRight className="h-3 w-3" />
      </Link>
    </>
  );
}

/* ───────────── Ops ───────────── */

export function QuickActionsWidget() {
  const actions = [
    { href: "/facturi/nou", label: "Factură", icon: FileText },
    { href: "/proforme/nou", label: "Proformă", icon: FileEdit },
    { href: "/avize/nou", label: "Aviz", icon: Truck },
    { href: "/chitante/nou", label: "Chitanță", icon: Receipt },
    { href: "/clienti", label: "Client", icon: UserPlus },
    { href: "/plati?tab=import", label: "Import bancar", icon: Landmark },
    { href: "/efactura", label: "e-Factura", icon: Send },
    { href: "/rapoarte?raport=vanzari", label: "Rapoarte", icon: BarChart3 },
    { href: "/facturi-recurente/nou", label: "Recurență", icon: Repeat },
    { href: "/cheltuieli", label: "Cheltuială", icon: TrendingDown },
    { href: "/gestionari", label: "NIR stoc", icon: Package },
    { href: "/plati/nou", label: "Plată", icon: Coins },
  ];
  return (
    <div
      className="grid gap-2 h-full"
      style={{
        gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
        gridTemplateRows: "repeat(4, minmax(0, 1fr))",
      }}
    >
      {actions.map((a) => (
        <Link
          key={a.href}
          href={a.href}
          className="group flex flex-col items-center justify-center gap-1.5 rounded-lg border border-neutral-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 p-2 transition-all hover:-translate-y-[1px] min-w-0"
          style={{ transition: "all 0.18s ease" }}
          onMouseEnter={(e) => {
            e.currentTarget.style.borderColor = "rgb(var(--primary-500) / 0.4)";
            e.currentTarget.style.boxShadow = "0 2px 8px rgb(var(--primary-500) / 0.08)";
            e.currentTarget.style.background = "rgb(var(--primary-500) / 0.04)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.borderColor = "";
            e.currentTarget.style.boxShadow = "";
            e.currentTarget.style.background = "";
          }}
        >
          <span
            className="grid h-8 w-8 shrink-0 place-items-center rounded-md transition"
            style={{ background: "rgb(var(--primary-500) / 0.1)", color: "rgb(var(--primary-600))" }}
          >
            <a.icon className="h-4 w-4" />
          </span>
          <span className="text-[0.6563rem] font-medium text-neutral-700 dark:text-neutral-300 text-center leading-tight group-hover:text-primary transition truncate max-w-full">
            {a.label}
          </span>
        </Link>
      ))}
    </div>
  );
}

/* ───────────── Cash Flow Forecast ───────────── */

export function CashFlowForecastWidget() {
  const threeMonthsAgo = new Date();
  threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 6);
  const iso = (d: Date) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return y + '-' + m + '-' + day;
  };

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'cashflow-forecast'],
    queryFn: () =>
      api.get<{ data: any[] }>(
        '/invoices?' +
          new URLSearchParams({
            limit: '500',
            direction: 'outgoing',
            type: 'factura',
            dateFrom: iso(threeMonthsAgo),
          }).toString()
      ),
    staleTime: 5 * 60 * 1000,
  });

  const forecast = useMemo(() => {
    if (!data?.data) return null;
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    // Build 13 buckets of 7 days each = 91 days
    const buckets: Array<{ dayStart: number; income: number; count: number }> = [];
    for (let i = 0; i < 13; i++) {
      buckets.push({ dayStart: i * 7, income: 0, count: 0 });
    }
    let inNext30 = 0, inNext60 = 0, inNext90 = 0, overdueTotal = 0;
    for (const inv of data.data as any[]) {
      if (!inv.dueDate) continue;
      if (inv.status === 'cancelled' || inv.status === 'storno' || inv.status === 'draft') continue;
      const remaining = Number(inv.total || 0) - Number(inv.paidAmount || 0);
      if (remaining <= 0) continue;
      const due = new Date(inv.dueDate);
      const dueDay = new Date(due.getFullYear(), due.getMonth(), due.getDate());
      const daysAhead = Math.floor((dueDay.getTime() - today.getTime()) / 86400000);
      if (daysAhead < 0) {
        overdueTotal += remaining;
        continue;
      }
      if (daysAhead >= 91) continue;
      const bucketIdx = Math.min(12, Math.floor(daysAhead / 7));
      buckets[bucketIdx]!.income += remaining;
      buckets[bucketIdx]!.count += 1;
      if (daysAhead < 30) inNext30 += remaining;
      if (daysAhead < 60) inNext60 += remaining;
      if (daysAhead < 90) inNext90 += remaining;
    }
    // Cumulative income curve for area chart
    let cum = 0;
    const points = buckets.map((b, i) => {
      cum += b.income;
      return { x: i, y: cum };
    });
    const maxY = Math.max(1, ...points.map((p) => p.y));
    return { buckets, points, maxY, inNext30, inNext60, inNext90, overdueTotal };
  }, [data]);

  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  if (isLoading || !forecast) {
    return <div className="animate-pulse h-32 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  }

  // Build SVG path for area chart — cumulative income (only real data, no mock outflows)
  const w = 600, h = 140;
  const pxAt = (x: number) => (x / 12) * w;
  const pyAt = (y: number) => h - 8 - (y / forecast.maxY) * (h - 28);
  const line = forecast.points.map((p, i) => (i === 0 ? 'M' : 'L') + pxAt(p.x) + ',' + pyAt(p.y)).join(' ');
  const area = line + ' L' + pxAt(12) + ',' + h + ' L0,' + h + ' Z';

  return (
    <>
      <svg viewBox={'0 0 ' + w + ' ' + h} className="w-full mt-2" style={{ height: 140 }} preserveAspectRatio="none">
        <defs>
          <linearGradient id="cashflow-in" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#10B981" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#10B981" stopOpacity="0" />
          </linearGradient>
          <linearGradient id="cashflow-out" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" style={{ stopColor: 'rgb(var(--primary-500))', stopOpacity: 0.25 }} />
            <stop offset="100%" style={{ stopColor: 'rgb(var(--primary-500))', stopOpacity: 0 }} />
          </linearGradient>
        </defs>
        {/* Grid */}
        <line x1="0" y1={h * 0.25} x2={w} y2={h * 0.25} stroke="#EDEAE3" strokeDasharray="2,3" />
        <line x1="0" y1={h * 0.5} x2={w} y2={h * 0.5} stroke="#EDEAE3" strokeDasharray="2,3" />
        <line x1="0" y1={h * 0.75} x2={w} y2={h * 0.75} stroke="#EDEAE3" strokeDasharray="2,3" />
        {/* Income area (green) — încasări așteptate cumulative */}
        <path d={area} fill="url(#cashflow-in)" />
        <path d={line} stroke="#10B981" strokeWidth="2" fill="none" />
        {/* Axis labels */}
        <text x="0" y={h - 2} fill="#9A9791" fontSize="9" fontFamily="var(--font-poppins), sans-serif">azi</text>
        <text x={pxAt(4)} y={h - 2} fill="#9A9791" fontSize="9" fontFamily="var(--font-poppins), sans-serif">+30z</text>
        <text x={pxAt(8)} y={h - 2} fill="#9A9791" fontSize="9" fontFamily="var(--font-poppins), sans-serif">+60z</text>
        <text x={pxAt(12) - 28} y={h - 2} fill="#9A9791" fontSize="9" fontFamily="var(--font-poppins), sans-serif">+90z</text>
      </svg>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-[0.6875rem] text-neutral-500 dark:text-neutral-400 mt-2">
        <span>
          <span className="inline-block w-2.5 h-0.5 bg-emerald-500 align-middle mr-1"></span>
          Încasări așteptate (90z): <b className="text-emerald-700 dark:text-emerald-400">{fmtShort(forecast.inNext90)}</b>
        </span>
        {forecast.overdueTotal > 0 && (
          <span>
            <span className="inline-block w-2.5 h-0.5 bg-red-500 align-middle mr-1"></span>
            Restanțe: <b className="text-red-700 dark:text-red-400">{fmtShort(forecast.overdueTotal)}</b>
          </span>
        )}
      </div>
      <div className="grid grid-cols-3 gap-2 mt-3">
        <div className="bg-emerald-50 dark:bg-emerald-950/30 rounded-md p-2">
          <div className="text-[0.5938rem] uppercase tracking-wider text-neutral-500 font-semibold">+30z</div>
          <div className="text-[0.7813rem] font-semibold text-emerald-700 dark:text-emerald-400">{fmtShort(forecast.inNext30).replace(' RON', '')}</div>
        </div>
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md p-2">
          <div className="text-[0.5938rem] uppercase tracking-wider text-neutral-500 font-semibold">+60z</div>
          <div className="text-[0.7813rem] font-semibold">{fmtShort(forecast.inNext60).replace(' RON', '')}</div>
        </div>
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-md p-2">
          <div className="text-[0.5938rem] uppercase tracking-wider text-neutral-500 font-semibold">+90z</div>
          <div className="text-[0.7813rem] font-semibold">{fmtShort(forecast.inNext90).replace(' RON', '')}</div>
        </div>
      </div>
    </>
  );
}

/* ───────────── What-if Scenarios ───────────── */

export function WhatIfWidget() {
  const { data: dashStats, isError: e1, refetch: r1 } = useDashboardStats();
  const { data: invoiceStats, isError: e2, refetch: r2 } = useInvoiceStats();
  const [recoveryPct, setRecoveryPct] = useState(50);
  const [paymentTerm, setPaymentTerm] = useState(30);
  const [recoveryDays] = useState(14);

  const baseline = useMemo(() => {
    // Cash baseline = revenue MTD (proxy pentru "money invoiced this month").
    // Outstanding = TOATE facturile neîncasate (sursă: /invoices/stats), nu doar overdue.
    // Asta e mai cinstit: scenariul "ce dacă recuperez X%" se aplică la TOATE neîncasatele.
    const revenue = Number(dashStats?.revenueThisMonth || 0);
    const outstanding = Number(invoiceStats?.unpaidTotal || 0);
    const cash = revenue - outstanding; // poate fi NEGATIV — afișăm explicit
    // DSO formula corectă: (unpaid / revenue MTD) × ziua curentă
    const dayOfMonth = new Date().getDate();
    const dso = revenue > 0 ? (outstanding / revenue) * dayOfMonth : 0;
    return { cash, outstanding, dso, invoiceCount: Number(invoiceStats?.unpaidCount || 0) };
  }, [dashStats, invoiceStats]);

  const scenario = useMemo(() => {
    // Recovery: bring back X% from outstanding
    const recovered = baseline.outstanding * (recoveryPct / 100);
    const newCash = baseline.cash + recovered;
    const newOutstanding = baseline.outstanding - recovered;
    // DSO delta: recovering early cuts average days by proportional factor
    const dsoReduction = baseline.dso * (recoveryPct / 100) * (14 / Math.max(14, baseline.dso));
    const newDso = Math.max(0, baseline.dso - dsoReduction);
    // Payment term impact — lower term = faster future income (heuristic estimate)
    const termFactor = paymentTerm / 30; // 1.0 = neutral
    const projectedDso = newDso * termFactor;
    return {
      cash: newCash,
      outstanding: newOutstanding,
      dso: projectedDso,
      recovered,
      deltaCash: newCash - baseline.cash,
      deltaDso: projectedDso - baseline.dso,
    };
  }, [baseline, recoveryPct, paymentTerm, recoveryDays]);

  const deltaCashPct = baseline.cash > 0 ? (scenario.deltaCash / baseline.cash) * 100 : 0;

  if (e1 || e2) return <WidgetErrorState onRetry={() => { r1(); r2(); }} />;

  return (
    <div className="mt-2 space-y-4">
      <p className="text-[0.7188rem] text-neutral-500 dark:text-neutral-400 leading-snug">
        Simulează scenarii financiare — trage slider-ele să vezi cum se schimbă cifrele.
      </p>

      {/* Slider 1 — Recovery */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[0.7188rem] text-neutral-700 dark:text-neutral-300 font-medium">
            Recuperez <b className="text-primary tabular-nums">{recoveryPct}%</b> din restanțe
          </span>
          <span className="text-[0.6563rem] text-neutral-500 ">
            în {recoveryDays} zile
          </span>
        </div>
        <input
          type="range"
          min="0"
          max="100"
          step="5"
          value={recoveryPct}
          onChange={(e) => setRecoveryPct(Number(e.target.value))}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="w-full accent-primary cursor-pointer"
        />
      </div>

      {/* Slider 2 — Payment term */}
      <div>
        <div className="flex items-center justify-between mb-1.5">
          <span className="text-[0.7188rem] text-neutral-700 dark:text-neutral-300 font-medium">
            Reduc termen de plată la <b className="text-primary tabular-nums">{paymentTerm}z</b>
          </span>
          <span className="text-[0.6563rem] text-neutral-500 ">
            curent: 30z mediu
          </span>
        </div>
        <input
          type="range"
          min="7"
          max="60"
          step="1"
          value={paymentTerm}
          onChange={(e) => setPaymentTerm(Number(e.target.value))}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => e.stopPropagation()}
          className="w-full accent-primary cursor-pointer"
        />
      </div>

      {/* Impact */}
      <div className="pt-3 border-t border-neutral-200 dark:border-neutral-800 space-y-2">
        <div className="text-[0.5938rem] uppercase tracking-wider text-neutral-500 font-semibold">Impact previzionat</div>

        {/* Cash */}
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-2.5">
          <div className="flex items-center justify-between text-[0.7188rem] mb-1">
            <span className="text-neutral-600 dark:text-neutral-400">Cash position</span>
            <span className={'font-semibold ' + (scenario.deltaCash > 0 ? 'text-emerald-600' : 'text-neutral-500')}>
              {scenario.deltaCash > 0 ? '+' : ''}{fmtShort(scenario.deltaCash).replace(' RON', '')}
            </span>
          </div>
          <div className="flex items-center gap-2 text-[0.7813rem] ">
            <span className="text-neutral-400 line-through tabular-nums">{fmtShort(baseline.cash).replace(' RON', '')}</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-neutral-400">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
            <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {fmtShort(scenario.cash).replace(' RON', '')}
            </span>
            <span className="text-neutral-500">RON</span>
          </div>
          {Math.abs(deltaCashPct) > 0.5 && (
            <div className="text-[0.6563rem] text-neutral-500 mt-0.5">
              {deltaCashPct > 0 ? '+' : ''}{deltaCashPct.toFixed(1)}% față de acum
            </div>
          )}
        </div>

        {/* DSO */}
        <div className="bg-neutral-50 dark:bg-neutral-800 rounded-lg p-2.5">
          <div className="flex items-center justify-between text-[0.7188rem] mb-1">
            <span className="text-neutral-600 dark:text-neutral-400">DSO (zile medii)</span>
            <span className={'font-semibold ' + (scenario.deltaDso < 0 ? 'text-emerald-600' : 'text-neutral-500')}>
              {scenario.deltaDso > 0 ? '+' : ''}{scenario.deltaDso.toFixed(1)}z
            </span>
          </div>
          <div className="flex items-center gap-2 text-[0.7813rem] ">
            <span className="text-neutral-400 line-through tabular-nums">{baseline.dso.toFixed(1)}z</span>
            <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" className="text-neutral-400">
              <line x1="5" y1="12" x2="19" y2="12" />
              <polyline points="12 5 19 12 12 19" />
            </svg>
            <span className="font-semibold tabular-nums text-neutral-900 dark:text-neutral-100">
              {scenario.dso.toFixed(1)}z
            </span>
          </div>
        </div>

        {/* Recovered amount highlight */}
        {scenario.recovered > 0 && (
          <div className="text-[0.6875rem] text-neutral-600 dark:text-neutral-400 bg-emerald-50 dark:bg-emerald-950/30 rounded-md px-2.5 py-1.5">
            <span className="text-emerald-700 dark:text-emerald-400 font-semibold">{fmtShort(scenario.recovered)}</span>{' '}
            recuperat în {recoveryDays} zile prin remindere și follow-up.
          </div>
        )}
      </div>
    </div>
  );
}

/* ───────────── VAT Donut ───────────── */

export function VatDonutWidget() {
  const { data: vat, isLoading, isError, refetch } = useVatSummary();

  if (isLoading) return <div className="animate-pulse h-32 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;

  // Tenant neplătitor TVA — ascund widget-ul cu mesaj clar
  if (!vat?.isVatPayer) {
    return (
      <div className="text-[0.75rem] text-neutral-500 dark:text-neutral-400 py-4 text-center">
        Tenant neplătitor TVA — widget-ul nu se aplică.
      </div>
    );
  }

  const collected = vat.collected.total;
  const deductible = vat.deductible.total;
  const toPay = vat.toPay;
  const total = collected + deductible;
  const collectedPct = total > 0 ? (collected / total) * 100 : 50;
  const deductiblePct = total > 0 ? (deductible / total) * 100 : 50;

  return (
    <>
      <div className="flex items-center gap-4 mt-3">
        <svg viewBox="0 0 42 42" className="w-[110px] h-[110px] shrink-0">
          <circle cx="21" cy="21" r="15.915" fill="none" stroke="#F4F4F3" strokeWidth="6" />
          <circle cx="21" cy="21" r="15.915" fill="none" style={{ stroke: 'rgb(var(--primary-500))' }} strokeWidth="6"
            strokeDasharray={collectedPct + ' ' + (100 - collectedPct)} strokeDashoffset="25" transform="rotate(-90 21 21)" />
          <circle cx="21" cy="21" r="15.915" fill="none" stroke="#0B6B3A" strokeWidth="6"
            strokeDasharray={deductiblePct + ' ' + (100 - deductiblePct)} strokeDashoffset={25 - collectedPct} transform="rotate(-90 21 21)" />
          <text x="21" y="20" textAnchor="middle" fontFamily="var(--font-poppins), sans-serif" fontSize="6" fontWeight="600" fill={toPay >= 0 ? '#0F0E0F' : '#0B6B3A'}>
            {fmtShort(Math.abs(toPay)).replace(' RON', '')}
          </text>
          <text x="21" y="26" textAnchor="middle" fontFamily="var(--font-poppins), sans-serif" fontSize="2.2" fill="#6B6962">
            {toPay >= 0 ? 'de plată' : 'de recuperat'}
          </text>
        </svg>
        <div className="flex-1 flex flex-col gap-1 text-xs">
          <div className="flex justify-between items-center py-0.5">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-primary" />Colectat
            </span>
            <span className="font-semibold">{fmtShort(collected).replace(' RON', '')}</span>
          </div>
          <div className="flex justify-between items-center py-0.5">
            <span className="inline-flex items-center gap-1.5">
              <span className="w-2 h-2 rounded-sm bg-emerald-700" />Deductibil
            </span>
            <span className="font-semibold">{fmtShort(deductible).replace(' RON', '')}</span>
          </div>
          <div className="flex justify-between items-center py-1.5 mt-1 border-t border-neutral-200 dark:border-neutral-800">
            <span className="font-semibold">{toPay >= 0 ? 'De plată' : 'De recuperat'}</span>
            <span className={'font-semibold text-[0.8125rem] ' + (toPay >= 0 ? 'text-primary' : 'text-emerald-700 dark:text-emerald-400')}>
              {fmtShort(Math.abs(toPay)).replace(' RON', '')}
            </span>
          </div>
        </div>
      </div>

      {/* Defalcare pe cote — DOAR dacă există mai multe cote */}
      {(vat.collected.breakdown.length > 1 || vat.deductible.breakdown.length > 1) && (
        <div className="mt-3 pt-2 border-t border-neutral-200 dark:border-neutral-800">
          <div className="text-[0.625rem] uppercase tracking-wider text-neutral-500 font-semibold mb-1.5">Defalcare pe cote</div>
          <div className="flex flex-wrap gap-x-3 gap-y-1 text-[0.6563rem]">
            {vat.collected.breakdown.map((b) => (
              <span key={'c-' + b.rate} className="text-neutral-600 dark:text-neutral-400">
                <span className="text-primary">●</span> {b.rate}%: <b className="">{fmtShort(b.amount).replace(' RON', '')}</b>
              </span>
            ))}
            {vat.deductible.breakdown.length > 0 && vat.collected.breakdown.length > 0 && <span className="text-neutral-300">|</span>}
            {vat.deductible.breakdown.map((b) => (
              <span key={'d-' + b.rate} className="text-neutral-600 dark:text-neutral-400">
                <span className="text-emerald-700">●</span> {b.rate}%: <b className="">{fmtShort(b.amount).replace(' RON', '')}</b>
              </span>
            ))}
          </div>
        </div>
      )}

      <div className="mt-2 pt-2 border-t border-neutral-200 dark:border-neutral-800 text-[0.6875rem] text-neutral-500">
        Termen D300: <b className={vat.daysUntilDeadline <= 3 ? 'text-primary' : ''}>{vat.declarationDeadline}</b> · {vat.daysUntilDeadline} {vat.daysUntilDeadline === 1 ? 'zi' : 'zile'} rămase
      </div>
      <div className="mt-1.5 text-[0.5938rem] text-neutral-400 italic leading-tight">
        Estimare bazată pe facturile înregistrate în SoftFactura. Pentru declarația D300 oficială, consultă contabilul.
      </div>
    </>
  );
}

/* ───────────── Calendar fiscal (termene) ───────────── */

export function FiscalCalendarWidget() {
  const { hasModule } = useHasModule('accounting');
  const { can } = usePermissions();
  const canRead = can('accounting.read');
  const { data, isLoading, isError } = useQuery<{
    items: Array<{
      id: string;
      kind: string;
      label: string;
      periodLabel: string;
      dueDateShifted: string;
      daysRemaining: number;
      status: string;
    }>;
  }>({
    queryKey: ['fiscal-calendar-widget'],
    queryFn: () => api.get('/accounting/fiscal-calendar'),
    staleTime: 5 * 60_000,
    enabled: hasModule && canRead,
  });

  if (!hasModule) {
    return (
      <WidgetTeaser
        moduleCode="accounting"
        hint="Termenele declarațiilor și plăților firmei, pe zile lucrătoare, cu bifare după depunere."
      />
    );
  }
  // Fara ramurile astea, eroarea/lipsa permisiunii ar arata ca "nimic scadent"
  // — un fals all-clear fiscal (review UI).
  if (!canRead) {
    return <div className="text-sm text-neutral-500 py-4">Nu ai permisiunea de a vedea calendarul fiscal al firmei.</div>;
  }
  if (isError) {
    return <div className="text-sm text-neutral-500 py-4">Nu am putut încărca termenele fiscale. Reîncearcă mai târziu.</div>;
  }
  if (isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;

  const pending = (data?.items || []).filter((i) => i.status === 'pending');
  const overdue = pending.filter((i) => i.daysRemaining < 0);
  // Randurile din aceeasi zi se grupeaza ("D300 + D390 + plăți · iunie 2026")
  // — decizia owner pe mockup. API-ul vine sortat pe termen.
  const byDay = new Map<string, typeof pending>();
  for (const i of pending.filter((x) => x.daysRemaining >= 0)) {
    const list = byDay.get(i.dueDateShifted) || [];
    list.push(i);
    byDay.set(i.dueDateShifted, list);
  }
  const dayGroups = [...byDay.entries()].slice(0, 4).map(([day, list]) => {
    const parts = list
      .filter((i) => i.kind !== 'plata')
      .map((i) => (i.label.includes(' — ') ? i.label.split(' — ')[0]!.trim() : i.label));
    if (list.some((i) => i.kind === 'plata')) parts.push('plăți');
    const periods = new Set(list.map((i) => i.periodLabel));
    const suffix = periods.size === 1 ? ` · ${[...periods][0]}` : '';
    return { day, daysRemaining: list[0]!.daysRemaining, text: `${parts.join(' + ')}${suffix}` };
  });

  const fmtDay = (iso: string) => `${iso.slice(8, 10)}.${iso.slice(5, 7)}`;
  const chipCls = (d: number) =>
    d <= 3
      ? 'bg-red-50 text-red-600 dark:bg-red-950 dark:text-red-400'
      : d <= 10
        ? 'bg-amber-50 text-amber-700 dark:bg-amber-950 dark:text-amber-400'
        : 'bg-neutral-100 text-neutral-600 dark:bg-neutral-800 dark:text-neutral-300';

  return (
    <>
      {overdue.length > 0 && (
        <div className="text-xs text-red-600 font-semibold mb-2">
          {overdue.length} {overdue.length === 1 ? 'termen depășit' : 'termene depășite'} — verifică în calendar
        </div>
      )}
      {dayGroups.length === 0 && overdue.length === 0 && (
        <div className="text-sm text-neutral-500 py-4">Nimic scadent în perioada următoare.</div>
      )}
      <div className="divide-y divide-neutral-100 dark:divide-neutral-800">
        {dayGroups.map((g) => (
          <div key={g.day} className="flex items-center justify-between py-1.5 text-[13px]">
            <span className="truncate mr-2">{g.text}</span>
            <span className={`text-xs font-bold px-2 py-0.5 rounded-full whitespace-nowrap ${chipCls(g.daysRemaining)}`}>
              {fmtDay(g.day)} · {g.daysRemaining === 0 ? 'azi' : `${g.daysRemaining} ${g.daysRemaining === 1 ? 'zi' : 'zile'}`}
            </span>
          </div>
        ))}
      </div>
      <Link href="/contabilitate/calendar" className="block text-xs text-primary-600 hover:underline mt-2">
        Vezi calendarul complet →
      </Link>
    </>
  );
}

/* ───────────── SPV Status ───────────── */

export function SpvStatusWidget() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['efactura', 'oauth-status'],
    queryFn: () => api.get<{ connected: boolean; expiresAt?: string | null; accountantEmail?: string | null }>('/efactura/oauth/status'),
    staleTime: 5 * 60 * 1000,
  });

  if (isLoading) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;

  const connected = !!data?.connected;
  const daysUntilExpiry = data?.expiresAt
    ? Math.ceil((new Date(data.expiresAt).getTime() - Date.now()) / 86400000)
    : null;

  return (
    <div className="flex flex-col gap-2 mt-2">
      <div
        className={
          'flex items-center gap-3 p-3 rounded-lg ' +
          (connected
            ? 'bg-emerald-50 dark:bg-emerald-950/30'
            : 'bg-orange-50 dark:bg-orange-950/30')
        }
      >
        <div
          className={
            'w-10 h-10 rounded-lg grid place-items-center ' +
            (connected ? 'bg-emerald-600 text-white' : 'bg-primary text-white')
          }
        >
          <Shield className="h-5 w-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className={'text-[0.8125rem] font-semibold ' + (connected ? 'text-emerald-700 dark:text-emerald-400' : 'text-primary')}>
            {connected ? 'Conectat SPV' : 'Neconectat SPV'}
          </div>
          <div className="text-[0.6875rem] text-neutral-500 dark:text-neutral-400 mt-0.5">
            {connected
              ? (daysUntilExpiry !== null && daysUntilExpiry < 30
                  ? 'Token expiră în ' + daysUntilExpiry + ' zile'
                  : 'Transmitere automată activă')
              : 'Conectează-ți certificatul digital pentru e-Factura'}
          </div>
        </div>
      </div>
      {data?.accountantEmail && (
        <div className="text-[0.6563rem] text-neutral-500">
          Contabil: <span className="">{data.accountantEmail}</span>
        </div>
      )}
      <Link
        href="/efactura"
        className="text-[0.6875rem] text-primary font-medium hover:underline inline-flex items-center gap-1 mt-1"
      >
        {connected ? 'Administrează conexiunea' : 'Conectează acum'} <ArrowUpRight className="h-3 w-3" />
      </Link>
    </div>
  );
}

/* ───────────── Rată încasare ───────────── */

export function CollectionRateWidget() {
  const { data: buckets, isLoading, isError, refetch } = useCollectionRate();

  const metrics = useMemo(() => {
    const arr = (buckets || []) as Array<{ month: string; label: string; invoiced: number; collected: number; rate: number }>;
    if (arr.length === 0) return { rate: 0, collected: 0, invoiced: 0, trend: [], delta: 0 };
    const current = arr[arr.length - 1]!;
    const previous = arr.length >= 2 ? arr[arr.length - 2]! : null;
    const delta = previous ? current.rate - previous.rate : 0;
    return {
      rate: current.rate,
      collected: current.collected,
      invoiced: current.invoiced,
      trend: arr,
      delta,
    };
  }, [buckets]);

  if (isLoading) return <div className="animate-pulse h-32 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;

  const sparklineW = 220;
  const sparklineH = 40;
  const maxRate = Math.max(100, ...metrics.trend.map((t) => t.rate));
  const points = metrics.trend.map((t, i) => {
    const x = (i / Math.max(1, metrics.trend.length - 1)) * sparklineW;
    const y = sparklineH - (t.rate / maxRate) * (sparklineH - 4) - 2;
    return x + ',' + y;
  }).join(' ');

  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] my-2 text-neutral-900 dark:text-neutral-100"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontWeight: 600, fontSize: '2.125rem' }}
      >
        {metrics.rate.toFixed(0)}
        <span className="font-normal text-neutral-500" style={{ fontSize: '1.125rem' }}>.{Math.round((metrics.rate % 1) * 10)}</span>
        <span className="ml-1.5 text-[0.75rem] font-medium text-neutral-400">%</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400 flex items-center gap-2">
        <span>{fmtShort(metrics.collected).replace(' RON','')} din {fmtShort(metrics.invoiced).replace(' RON','')} RON luna curenta</span>
      </div>
      {metrics.trend.length > 1 && (
        <svg
          viewBox={'0 0 ' + sparklineW + ' ' + sparklineH}
          className="w-full mt-2"
          style={{ height: sparklineH }}
          preserveAspectRatio="none"
        >
          <polyline fill="none" stroke="#10B981" strokeWidth="2" points={points} />
          {metrics.trend.map((t, i) => {
            const x = (i / Math.max(1, metrics.trend.length - 1)) * sparklineW;
            const y = sparklineH - (t.rate / maxRate) * (sparklineH - 4) - 2;
            const isLast = i === metrics.trend.length - 1;
            return (
              <circle key={i} cx={x} cy={y} r={isLast ? 3 : 2} fill={isLast ? '#10B981' : '#A7F3D0'} />
            );
          })}
        </svg>
      )}
    </>
  );
}

/* ───────────── Comparație YoY ───────────── */

export function YoyComparisonWidget() {
  const { data: chart, isLoading, isError, refetch } = useRevenueChart(12);

  const metrics = useMemo(() => {
    if (!chart || chart.length < 2) return null;
    const months = chart as any[];

    // Pro-rate luna curentă (e încă în curs) — scale by zile_in_luna / ziua_curenta
    const now = new Date();
    const dayOfMonth = now.getDate();
    const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const currentRaw = Number(months[months.length - 1]?.revenue || 0);
    const currentProjected = dayOfMonth > 0 ? (currentRaw / dayOfMonth) * daysInCurrentMonth : currentRaw;

    // Media lunilor anterioare — doar lunile cu revenue > 0 (tenant care abia a început va avea zero-uri irelevante)
    const prevMonths = months.slice(0, -1);
    const prevWithRevenue = prevMonths.filter((m: any) => Number(m.revenue || 0) > 0);
    const avg = prevWithRevenue.length > 0
      ? prevWithRevenue.reduce((s: number, m: any) => s + Number(m.revenue || 0), 0) / prevWithRevenue.length
      : 0;
    const delta = avg > 0 ? ((currentProjected - avg) / avg) * 100 : 0;
    const monthsCounted = prevWithRevenue.length;
    return { current: currentRaw, currentProjected, avg, delta, months, monthsCounted, dayOfMonth, daysInCurrentMonth };
  }, [chart]);

  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  if (isLoading || !metrics) {
    return <div className="animate-pulse h-32 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  }
  // Insufficient data — afișez doar luna curentă fără comparativ
  if (metrics.monthsCounted < 2) {
    return (
      <>
        <div className="flex items-baseline gap-2 my-2">
          <span
            className="font-medium leading-none tracking-[-0.03em] text-neutral-900 dark:text-neutral-100"
            style={{ fontFamily: 'var(--font-poppins), sans-serif', fontSize: '1.75rem' }}
          >
            {fmtShort(metrics.current).replace(' RON', '')}
          </span>
          <span className="text-[0.6875rem] text-neutral-400">RON · luna curentă (în curs)</span>
        </div>
        <div className="text-[0.7188rem] text-neutral-500">Date insuficiente pentru comparație (necesită ≥2 luni complete)</div>
      </>
    );
  }

  const maxVal = Math.max(1, ...metrics.months.map((m: any) => Number(m.revenue) || 0));
  const deltaColor = metrics.delta > 0 ? 'text-emerald-600' : metrics.delta < 0 ? 'text-red-600' : 'text-neutral-500';

  return (
    <>
      <div className="flex items-baseline gap-2 my-2">
        <span
          className="font-medium leading-none tracking-[-0.03em] text-neutral-900 dark:text-neutral-100"
          style={{ fontFamily: 'var(--font-poppins), sans-serif', fontSize: '1.75rem' }}
        >
          {fmtShort(metrics.current).replace(' RON', '')}
        </span>
        <span className="text-[0.6875rem] text-neutral-400">RON · luna curentă (până azi)</span>
      </div>
      <div className="text-[0.6563rem] text-neutral-500 mb-1">
        Proiectat la sfârșitul lunii: <b>{fmtShort(metrics.currentProjected).replace(' RON', '')} RON</b>
      </div>
      <div className={'text-[0.7188rem] font-semibold ' + deltaColor + ' flex items-center gap-1'}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          {metrics.delta > 0 ? (
            <polyline points="18 15 12 9 6 15" />
          ) : metrics.delta < 0 ? (
            <polyline points="6 9 12 15 18 9" />
          ) : (
            <line x1="5" y1="12" x2="19" y2="12" />
          )}
        </svg>
        {metrics.delta > 0 ? '+' : ''}{metrics.delta.toFixed(1)}% față de media pe {metrics.monthsCounted} luni
      </div>
      <div className="flex items-end gap-1 h-[60px] mt-3">
        {metrics.months.map((m: any, i: number) => {
          const h = (Number(m.revenue) || 0) / maxVal * 100;
          const isCurrent = i === metrics.months.length - 1;
          return (
            <div
              key={i}
              className={'flex-1 rounded-t-sm transition-all ' + (isCurrent ? 'bg-primary' : 'bg-neutral-200 dark:bg-neutral-700')}
              style={{ height: h + '%', minHeight: '3%' }}
              title={m.month + ': ' + fmtShort(Number(m.revenue))}
            />
          );
        })}
      </div>
      {/* month e localizat („aug. 2025") — vechiul .slice(5) presupunea YYYY-MM
          și afișa anul în loc de lună; afișăm eticheta întreagă (interval). */}
      <div className="flex justify-between text-[0.5938rem] text-neutral-400 mt-1">
        <span>{metrics.months[0]?.month || ''}</span>
        <span>{metrics.months[metrics.months.length - 1]?.month || ''}</span>
      </div>
    </>
  );
}

/* ───────────── Tendință restanțe ───────────── */

export function OverdueTrendWidget() {
  // Sursă endpoint-ul dedicat: pentru fiecare lună din ultimele 6, snapshot-ul restanțelor
  // active la SFÂRȘITUL acelei luni. Diferit de gruparea pe luna scadenței.
  const { data, isLoading, isError, refetch } = useOverdueTrend();
  const buckets = (data || []) as Array<{ month: string; label: string; total: number; count: number }>;

  if (isLoading) return <div className="animate-pulse h-32 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;

  const currentTotal = buckets.length > 0 ? (buckets[buckets.length - 1]!.total) : 0;
  const prevTotal = buckets.length > 1 ? (buckets[buckets.length - 2]!.total) : 0;
  const delta = prevTotal > 0 ? ((currentTotal - prevTotal) / prevTotal) * 100 : 0;
  const maxVal = Math.max(1, ...buckets.map((b) => b.total));

  return (
    <>
      <div className="flex items-baseline gap-2 my-2">
        <span
          className="font-medium leading-none tracking-[-0.03em]"
          style={{
            fontFamily: 'var(--font-poppins), sans-serif',
            fontSize: '1.75rem',
            color: currentTotal > 0 ? 'rgb(185, 28, 28)' : 'rgb(15,14,15)',
          }}
        >
          {fmtShort(currentTotal).replace(' RON', '')}
        </span>
        <span className="text-[0.6875rem] text-neutral-400">RON restanțe</span>
      </div>
      <div className={'text-[0.7188rem] font-semibold flex items-center gap-1 ' + (delta > 0 ? 'text-red-600' : delta < 0 ? 'text-emerald-600' : 'text-neutral-500')}>
        <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3">
          {delta > 0 ? (
            <polyline points="18 15 12 9 6 15" />
          ) : delta < 0 ? (
            <polyline points="6 9 12 15 18 9" />
          ) : (
            <line x1="5" y1="12" x2="19" y2="12" />
          )}
        </svg>
        {delta > 0 ? '+' : ''}{delta.toFixed(1)}% vs luna trecută · {delta < 0 ? 'îmbunătățire' : delta > 0 ? 'creștere' : 'stabil'}
      </div>
      <div className="flex items-end gap-1 h-[60px] mt-3">
        {buckets.map((b, i) => {
          const h = b.total / maxVal * 100;
          const isCurrent = i === buckets.length - 1;
          return (
            <div
              key={i}
              className={'flex-1 rounded-t-sm ' + (isCurrent ? 'bg-red-500' : 'bg-neutral-200 dark:bg-neutral-700')}
              style={{ height: Math.max(3, h) + '%' }}
              title={b.label + ': ' + fmtShort(b.total)}
            />
          );
        })}
      </div>
      <div className="flex justify-between text-[0.5938rem] text-neutral-400 mt-1">
        {buckets.map((b, i) => (
          <span key={i}>{b.label.slice(0, 3)}</span>
        ))}
      </div>
    </>
  );
}

/* ───────────── Recurențe următoare (programate) ───────────── */

export function UpcomingRecurringWidget() {
  const { hasModule } = useHasModule('invoicing_pro');

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['recurring-invoices'],
    queryFn: () => api.get<any[]>('/recurring-invoices'),
    staleTime: 5 * 60 * 1000,
    enabled: hasModule,
  });

  const upcoming = useMemo(() => {
    if (!data) return [];
    const now = new Date();
    const limit = new Date(now.getTime() + 14 * 86400000); // next 14 days
    return (data as any[])
      .filter((r) => r.isActive && r.nextIssueDate)
      .map((r) => ({
        ...r,
        issueDate: new Date(r.nextIssueDate),
      }))
      .filter((r) => r.issueDate >= now && r.issueDate <= limit)
      .sort((a, b) => a.issueDate.getTime() - b.issueDate.getTime())
      .slice(0, 5);
  }, [data]);

  if (!hasModule) {
    return (
      <WidgetTeaser
        moduleCode="invoicing_pro"
        hint="Vezi facturile programate să fie emise automat în următoarele 14 zile."
      />
    );
  }

  if (isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;

  if (upcoming.length === 0) {
    return (
      <EmptyList
        title="Recurențe · 14 zile"
        hint={EMPTY_COPY.upcomingRecurring.hint}
        ctaLabel="Creează recurență"
        ctaHref="/facturi-recurente"
      />
    );
  }

  const freqLabels: Record<string, string> = {
    weekly: 'Săptămânal',
    monthly: 'Lunar',
    quarterly: 'Trimestrial',
    yearly: 'Anual',
  };

  return (
    <div className="flex flex-col">
      {upcoming.map((r: any) => {
        const daysUntil = Math.max(0, Math.ceil((r.issueDate.getTime() - Date.now()) / 86400000));
        const isSoon = daysUntil <= 2;
        return (
          <Link
            key={r.id}
            href="/facturi-recurente"
            className="flex items-center gap-3 py-2.5 border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-800/40 -mx-2 px-2 rounded"
          >
            <div
              className={
                'w-11 h-11 shrink-0 rounded-lg flex flex-col items-center justify-center ' +
                (isSoon
                  ? 'bg-orange-50 dark:bg-orange-950/30 text-primary'
                  : 'bg-neutral-100 dark:bg-neutral-800 text-neutral-500')
              }
            >
              <span className="font-bold text-[0.875rem] leading-none">{daysUntil === 0 ? 'azi' : daysUntil + 'z'}</span>
              <span className="text-[0.5rem] uppercase tracking-wider font-semibold mt-0.5">
                {isSoon ? 'apropiat' : r.issueDate.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' })}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="text-[0.7813rem] font-semibold text-neutral-900 dark:text-neutral-100 truncate">
                {r.client?.name || 'Client necunoscut'}
              </div>
              <div className="text-[0.6875rem] text-neutral-500">
                {freqLabels[r.frequency] || r.frequency}
                {r.template?.lines && r.template.lines.length > 0 && (
                  <> · {r.template.lines.length} {r.template.lines.length === 1 ? 'linie' : 'linii'}</>
                )}
              </div>
            </div>
          </Link>
        );
      })}
    </div>
  );
}

/* ───────────── Recurențe detectate (AI) ───────────── */

export function DetectedRecurringWidget() {
  const { data: subStatus } = useSubscriptionStatus();
  const hasPro = !!subStatus && (subStatus.subscription.addons as string[]).includes('invoicing_pro');

  const sixMonthsAgo = useMemo(() => {
    const d = new Date();
    d.setMonth(d.getMonth() - 6);
    return d.toISOString().slice(0, 10);
  }, []);

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'detected-recurring'],
    queryFn: () =>
      api.get<{ data: any[] }>(
        '/invoices?' + new URLSearchParams({
          limit: '200',
          direction: 'outgoing',
          type: 'factura',
          dateFrom: sixMonthsAgo,
        }).toString()
      ),
    staleTime: 10 * 60 * 1000,
    enabled: hasPro,
  });

  const candidates = useMemo(() => {
    if (!data?.data) return [];
    // Detecție pattern recurring: client cu ≥2 facturi care îndeplinesc TOATE:
    //   1. amount_variance ≤ 12% (sume similare, nu doar coincidență)
    //   2. interval consistent (variance ≤ 25%)
    //   3. interval cade într-un pattern recunoscut (lunar, trimestrial, semestrial, anual)
    const byClient = new Map<string, any[]>();
    for (const inv of data.data as any[]) {
      if (!inv.issueDate || inv.status === 'draft' || inv.status === 'cancelled' || inv.status === 'storno') continue;
      const cid = inv.clientId || inv.client?.id;
      if (!cid) continue;
      const arr = byClient.get(cid) || [];
      arr.push(inv);
      byClient.set(cid, arr);
    }
    const results: any[] = [];
    for (const [clientId, arr] of byClient) {
      if (arr.length < 2) continue;
      arr.sort((a, b) => (a.issueDate < b.issueDate ? -1 : 1));
      const totals = arr.map((a) => Number(a.total) || 0);
      const avg = totals.reduce((s, x) => s + x, 0) / totals.length;
      if (avg <= 0) continue;

      // Check 1: amount variance ≤12%
      const maxAmountVar = Math.max(...totals.map((t) => Math.abs(t - avg) / avg));
      if (maxAmountVar > 0.12) continue;

      // Check 2 & 3: interval consistency + recognized pattern
      const intervals: number[] = [];
      for (let i = 1; i < arr.length; i++) {
        const days = (new Date(arr[i].issueDate).getTime() - new Date(arr[i - 1].issueDate).getTime()) / 86400000;
        intervals.push(days);
      }
      if (intervals.length === 0) continue;
      const avgInterval = intervals.reduce((s, x) => s + x, 0) / intervals.length;
      const maxIntervalVar = Math.max(...intervals.map((d) => Math.abs(d - avgInterval) / avgInterval));
      if (maxIntervalVar > 0.25) continue;

      // Detect frequency from average interval
      let frequency: 'saptamanal' | 'bisaptamanal' | 'lunar' | 'trimestrial' | 'semestrial' | 'anual' | null = null;
      if (avgInterval >= 6 && avgInterval <= 8) frequency = 'saptamanal';
      else if (avgInterval >= 13 && avgInterval <= 16) frequency = 'bisaptamanal';
      else if (avgInterval >= 25 && avgInterval <= 37) frequency = 'lunar';
      else if (avgInterval >= 80 && avgInterval <= 100) frequency = 'trimestrial';
      else if (avgInterval >= 165 && avgInterval <= 195) frequency = 'semestrial';
      else if (avgInterval >= 350 && avgInterval <= 380) frequency = 'anual';
      if (!frequency) continue; // intervalul nu cade într-un pattern recunoscut

      results.push({
        clientId,
        clientName: arr[arr.length - 1]?.client?.name || 'Client',
        count: arr.length,
        avgAmount: avg,
        frequency,
        lastInvoiceId: arr[arr.length - 1]?.id,
      });
    }
    return results.sort((a, b) => b.count - a.count).slice(0, 4);
  }, [data]);

  if (!hasPro) {
    return (
      <WidgetTeaser
        moduleCode="invoicing_pro"
        hint="Detectare AI a clienților cu facturare repetitivă — sugestii automate din istoric."
      />
    );
  }

  if (isLoading) return <div className="animate-pulse h-24 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;

  if (candidates.length === 0) {
    return (
      <EmptyList
        title="Pattern-uri recurente"
        hint={EMPTY_COPY.detectedRecurring.hint}
      />
    );
  }

  return (
    <>
      <div className="text-[0.7188rem] text-neutral-500 dark:text-neutral-400 mb-2">
        <b className="text-primary">{candidates.length}</b> {candidates.length === 1 ? 'client detectat' : 'clienți detectați'} cu facturi repetitive. Transformă în recurențe automate.
      </div>
      <div className="flex flex-col">
        {candidates.map((c: any, idx: number) => {
          const freqMap: Record<string, string> = {
            saptamanal: 'weekly',
            bisaptamanal: 'weekly',
            lunar: 'monthly',
            trimestrial: 'quarterly',
            semestrial: 'quarterly',
            anual: 'yearly',
          };
          const freqUrl = freqMap[c.frequency] || 'monthly';
          return (
          <Link
            key={c.clientId}
            href={'/facturi-recurente/nou?fromInvoice=' + c.lastInvoiceId + '&freq=' + freqUrl}
            className="flex items-center gap-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-800/40 -mx-2 px-2 rounded"
          >
            <span
              className="text-neutral-400 italic w-5 shrink-0 text-center"
              style={{ fontStyle: 'italic', fontWeight: 500, fontSize: '0.875rem' }}
            >
              {(idx + 1).toString()}
            </span>
            <div className="flex-1 min-w-0">
              <div className="text-[0.7813rem] font-semibold truncate">{c.clientName}</div>
              <div className="text-[0.6563rem] text-neutral-500">{c.count} facturi · medie {fmtShort(c.avgAmount).replace(' RON', '')} RON</div>
            </div>
            <span className="text-[0.5625rem] font-semibold uppercase tracking-[0.06em] px-1.5 py-0.5 rounded-sm bg-violet-100 dark:bg-violet-900/30 text-violet-700 dark:text-violet-400 shrink-0" style={{ fontFamily: 'var(--font-poppins), sans-serif' }}>
              {c.frequency}
            </span>
            <ArrowUpRight className="h-3.5 w-3.5 text-neutral-400" />
          </Link>
          );
        })}
      </div>
    </>
  );
}

/* ───────────── Alerts Center ───────────── */

export function AlertsCenterWidget() {
  const { data: overdue, isError: e1, refetch: r1 } = useOverdueInvoices();
  const { data: deadlines, isError: e2, refetch: r2 } = useQuery({
    queryKey: ['efactura', 'deadlines'],
    queryFn: () => api.get<Array<{ invoiceId: string; series: string; number: number; workingDaysRemaining: number }>>('/efactura/deadlines'),
    staleTime: 5 * 60 * 1000,
  });
  const { data: efacturaStats, isError: e3, refetch: r3 } = useQuery({
    queryKey: ['efactura', 'stats'],
    queryFn: () => api.get<{ rejected: number; pendingOverdue: number }>('/efactura/stats'),
    staleTime: 5 * 60 * 1000,
  });

  const alerts = useMemo(() => {
    const items: Array<{ id: string; severity: 'critical' | 'warning' | 'info'; title: string; desc: string; href?: string }> = [];

    // Urgent ANAF deadlines
    const urgentAnaf = (deadlines || []).filter((d) => d.workingDaysRemaining <= 2);
    if (urgentAnaf.length > 0) {
      items.push({
        id: 'anaf-urgent',
        severity: 'critical',
        title: urgentAnaf.length + ' ' + (urgentAnaf.length === 1 ? 'factură' : 'facturi') + ' cu termen ANAF &lt;48h',
        desc: urgentAnaf
          .map((d) => (d.series ?? '') + (d.number != null ? String(d.number).padStart(5, '0') : ''))
          .filter((label) => label.length > 0)
          .join(', '),
        href: '/facturi',
      });
    }

    // Rejected e-Factura
    if (efacturaStats && efacturaStats.rejected > 0) {
      items.push({
        id: 'rejected',
        severity: 'critical',
        title: efacturaStats.rejected + ' ' + (efacturaStats.rejected === 1 ? 'factură respinsă' : 'facturi respinse') + ' ANAF',
        desc: 'Verifică și retrimite',
        href: '/efactura',
      });
    }

    // Pending overdue (not submitted to SPV)
    if (efacturaStats && efacturaStats.pendingOverdue > 0) {
      items.push({
        id: 'pending',
        severity: 'warning',
        title: efacturaStats.pendingOverdue + ' facturi netransmise la SPV',
        desc: 'Emise de mai mult de 3 zile',
        href: '/efactura',
      });
    }

    // Overdue invoices critical (>60 days)
    const severeOverdue = (overdue || []).filter((inv: any) => {
      if (!inv.dueDate) return false;
      const days = Math.floor((Date.now() - new Date(inv.dueDate).getTime()) / 86400000);
      return days > 60;
    });
    if (severeOverdue.length > 0) {
      items.push({
        id: 'severe-overdue',
        severity: 'warning',
        title: severeOverdue.length + ' ' + (severeOverdue.length === 1 ? 'factură' : 'facturi') + ' restante peste 60 zile',
        desc: 'Consideră follow-up direct sau reziliere contract',
        href: '/facturi?filter=overdue',
      });
    }

    return items;
  }, [overdue, deadlines, efacturaStats]);

  if (e1 || e2 || e3) return <WidgetErrorState onRetry={() => { r1(); r2(); r3(); }} />;

  if (alerts.length === 0) {
    return (
      <div className="py-6 flex flex-col items-center gap-2.5">
        <div className="flex items-center gap-2">
          <span className="relative flex w-2 h-2">
            <span className="absolute inset-0 rounded-full bg-emerald-500/40 animate-ping" />
            <span className="relative w-2 h-2 rounded-full bg-emerald-500" />
          </span>
          <span
            className="text-[0.6563rem] font-semibold uppercase tracking-[0.12em] text-emerald-700 dark:text-emerald-400"
            style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
          >
            niciun semnal critic
          </span>
        </div>
        <p className="text-[0.75rem] text-neutral-500 dark:text-neutral-400 text-center max-w-[260px] leading-snug">
          {EMPTY_COPY.alerts.hint}
        </p>
      </div>
    );
  }

  const severityStyle: Record<string, string> = {
    critical: 'bg-red-50 dark:bg-red-950/30 border-red-200 dark:border-red-900/50',
    warning: 'bg-orange-50 dark:bg-orange-950/30 border-orange-200 dark:border-orange-900/50',
    info: 'bg-blue-50 dark:bg-blue-950/30 border-blue-200 dark:border-blue-900/50',
  };
  const severityDot: Record<string, string> = {
    critical: 'bg-red-500',
    warning: 'bg-orange-500',
    info: 'bg-blue-500',
  };

  return (
    <div className="flex flex-col gap-2 mt-2">
      {alerts.map((a) => {
        const content = (
          <>
            <span className={'w-2 h-2 rounded-full shrink-0 mt-1.5 ' + severityDot[a.severity]} />
            <div className="flex-1 min-w-0">
              <div className="text-[0.7813rem] font-semibold text-neutral-900 dark:text-neutral-100" dangerouslySetInnerHTML={{ __html: a.title }} />
              <div className="text-[0.6875rem] text-neutral-500 dark:text-neutral-400 mt-0.5 leading-snug">{a.desc}</div>
            </div>
            {a.href && <ArrowUpRight className="h-3.5 w-3.5 text-neutral-400 shrink-0" />}
          </>
        );
        const baseCls = 'flex gap-2.5 p-2.5 border rounded-lg transition-colors ' + severityStyle[a.severity] + ' hover:shadow-[0_2px_8px_rgba(0,0,0,0.04)]';
        return a.href ? (
          <Link key={a.id} href={a.href} className={baseCls}>
            {content}
          </Link>
        ) : (
          <div key={a.id} className={baseCls}>
            {content}
          </div>
        );
      })}
    </div>
  );
}

/* ───────────── Activity Feed ───────────── */

interface ActivityItem {
  id: string;
  kind: 'invoice' | 'proforma' | 'aviz' | 'storno' | 'expense' | 'receipt' | 'payment' | 'nir';
  title: string;
  subtitle: string;
  amount?: number;
  href?: string;
  timestamp: Date;
}

const KIND_META: Record<string, { label: string; icon: string; color: string; bg: string }> = {
  invoice: { label: 'Factură', icon: 'F', color: 'text-blue-600 dark:text-blue-400', bg: 'bg-blue-50 dark:bg-blue-950/30' },
  proforma: { label: 'Proforma', icon: 'P', color: 'text-purple-600 dark:text-purple-400', bg: 'bg-purple-50 dark:bg-purple-950/30' },
  aviz: { label: 'Aviz', icon: 'A', color: 'text-cyan-600 dark:text-cyan-400', bg: 'bg-cyan-50 dark:bg-cyan-950/30' },
  storno: { label: 'Storno', icon: 'S', color: 'text-red-600 dark:text-red-400', bg: 'bg-red-50 dark:bg-red-950/30' },
  expense: { label: 'Cheltuială', icon: 'C', color: 'text-orange-600 dark:text-orange-400', bg: 'bg-orange-50 dark:bg-orange-950/30' },
  receipt: { label: 'Chitanță', icon: 'K', color: 'text-indigo-600 dark:text-indigo-400', bg: 'bg-indigo-50 dark:bg-indigo-950/30' },
  payment: { label: 'Plată', icon: 'P', color: 'text-emerald-600 dark:text-emerald-400', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
  nir: { label: 'NIR', icon: 'N', color: 'text-amber-600 dark:text-amber-400', bg: 'bg-amber-50 dark:bg-amber-950/30' },
};

function relativeTime(date: Date): string {
  const diff = Math.floor((Date.now() - date.getTime()) / 1000);
  if (diff < 60) return 'acum';
  if (diff < 3600) return Math.floor(diff / 60) + ' min';
  if (diff < 86400) return Math.floor(diff / 3600) + ' ore';
  if (diff < 2 * 86400) return 'ieri';
  if (diff < 7 * 86400) return Math.floor(diff / 86400) + ' zile';
  return date.toLocaleDateString('ro-RO', { day: '2-digit', month: '2-digit' });
}

export function ActivityFeedWidget() {
  const invoicesQ = useQuery({
    queryKey: ['activity', 'invoices'],
    queryFn: () => api.get<{ data: any[] }>('/invoices?limit=15&page=1'),
    staleTime: 2 * 60 * 1000,
  });
  const receiptsQ = useQuery({
    queryKey: ['activity', 'receipts'],
    queryFn: () => api.get<{ data: any[] } | any[]>('/receipts?limit=10').catch(() => ({ data: [] })),
    staleTime: 2 * 60 * 1000,
  });
  const paymentsQ = useQuery({
    queryKey: ['activity', 'payments'],
    queryFn: () => api.get<{ data: any[] } | any[]>('/payments?limit=10').catch(() => ({ data: [] })),
    staleTime: 2 * 60 * 1000,
  });
  const { hasModule: hasStockModule, isLoading: subLoading } = useHasModule('stock');
  const nirQ = useQuery({
    queryKey: ['activity', 'nir'],
    queryFn: () => api.get<{ data: any[] } | any[]>('/stock/documents?limit=10&type=nir').catch(() => ({ data: [] })),
    staleTime: 2 * 60 * 1000,
    enabled: !subLoading && hasStockModule, // wait sub load
  });

  const items = useMemo<ActivityItem[]>(() => {
    const all: ActivityItem[] = [];

    // Invoices (includes all types)
    const invData = Array.isArray(invoicesQ.data) ? invoicesQ.data : invoicesQ.data?.data || [];
    for (const inv of invData as any[]) {
      const direction = inv.direction || 'outgoing';
      const type = inv.type || 'factura';
      let kind: ActivityItem['kind'] = 'invoice';
      if (type === 'proforma') kind = 'proforma';
      else if (type === 'aviz') kind = 'aviz';
      else if (type === 'storno') kind = 'storno';
      else if (direction === 'incoming') kind = 'expense';
      else kind = 'invoice';
      all.push({
        id: 'inv-' + inv.id,
        kind,
        title: (inv.series || '') + String(inv.number || 0).padStart(5, '0'),
        subtitle: inv.client?.name || inv.supplierName || '—',
        amount: Number(inv.total || 0),
        href: direction === 'incoming' ? ('/cheltuieli?view=' + inv.id) : ('/facturi?view=' + inv.id),
        timestamp: new Date(inv.createdAt || inv.issueDate),
      });
    }

    // Receipts
    const rcpData = Array.isArray(receiptsQ.data) ? receiptsQ.data : receiptsQ.data?.data || [];
    for (const r of rcpData as any[]) {
      all.push({
        id: 'rcp-' + r.id,
        kind: 'receipt',
        title: (r.series || 'K') + String(r.number || 0).padStart(5, '0'),
        subtitle: r.client?.name || r.invoiceNumber || '—',
        amount: Number(r.total || r.amount || 0),
        href: '/chitante/' + r.id,
        timestamp: new Date(r.createdAt || r.issueDate || r.date),
      });
    }

    // Payments — payments includ invoice cu client (filtrate direction='outgoing' la API)
    const payData = Array.isArray(paymentsQ.data) ? paymentsQ.data : paymentsQ.data?.data || [];
    for (const p of payData as any[]) {
      // Subtitle: client + nr factură pentru context complet
      const clientName = p.invoice?.client?.name || p.client?.name || p.clientName || '';
      const invoiceLabel = p.invoice
        ? (p.invoice.series || '') + String(p.invoice.number || 0).padStart(5, '0')
        : (p.invoiceNumber || '');
      const subtitle = [clientName, invoiceLabel].filter(Boolean).join(' · ') || '—';
      all.push({
        id: 'pay-' + p.id,
        kind: 'payment',
        title: 'Plată ' + (p.method === 'transfer_bancar' ? 'OP' : p.method === 'numerar' ? 'numerar' : p.method === 'card' ? 'card' : p.method || ''),
        subtitle,
        amount: Number(p.amount || 0),
        href: '/incasari',
        timestamp: new Date(p.createdAt || p.date || p.paymentDate),
      });
    }

    // NIR
    const nirData = Array.isArray(nirQ.data) ? nirQ.data : nirQ.data?.data || [];
    for (const n of nirData as any[]) {
      if (n.type && n.type !== 'nir') continue;
      all.push({
        id: 'nir-' + n.id,
        kind: 'nir',
        title: 'NIR ' + (n.series || '') + String(n.number || 0).padStart(5, '0'),
        subtitle: n.supplier?.name || n.supplierName || n.warehouse?.name || '—',
        amount: Number(n.totalValue || n.total || 0),
        href: '/receptie?view=' + n.id,
        timestamp: new Date(n.createdAt || n.date),
      });
    }

    // Sort by timestamp desc, top 10
    return all
      .filter((i) => !isNaN(i.timestamp.getTime()))
      .sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime())
      .slice(0, 10);
  }, [invoicesQ.data, receiptsQ.data, paymentsQ.data, nirQ.data]);

  const isLoading = invoicesQ.isLoading || receiptsQ.isLoading || paymentsQ.isLoading;

  if (isLoading && items.length === 0) {
    return <div className="animate-pulse h-32 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  }

  if (invoicesQ.isError || receiptsQ.isError || paymentsQ.isError) {
    return <WidgetErrorState onRetry={() => { invoicesQ.refetch(); receiptsQ.refetch(); paymentsQ.refetch(); }} />;
  }

  if (items.length === 0) {
    return (
      <EmptyList
        title="Activitate recentă"
        hint={EMPTY_COPY.activityFeed.hint}
      />
    );
  }

  return (
    <div className="flex flex-col">
      {items.map((item) => {
        const meta = KIND_META[item.kind] || KIND_META.invoice;
        const content = (
          <>
            <div
              className={
                'w-9 h-9 shrink-0 rounded-lg font-bold text-[0.8125rem] grid place-items-center ' +
                meta!.bg + ' ' + meta!.color
              }
              style={{ fontFamily: 'var(--font-poppins), sans-serif' }}
            >
              {meta!.icon}
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 text-[0.7813rem]">
                <span className="font-semibold text-neutral-900 dark:text-neutral-100 ">{item.title}</span>
                <span className={'text-[0.5938rem] uppercase tracking-wider font-semibold ' + meta!.color}>
                  {meta!.label}
                </span>
              </div>
              <div className="text-[0.6875rem] text-neutral-500 dark:text-neutral-400 truncate">{item.subtitle}</div>
            </div>
            <div className="flex flex-col items-end shrink-0">
              {item.amount !== undefined && item.amount > 0 && (
                <span className="font-semibold text-[0.75rem] tabular-nums">
                  {fmtShort(item.amount).replace(' RON', '')}
                </span>
              )}
              <span className="text-[0.625rem] text-neutral-400 ">{relativeTime(item.timestamp)}</span>
            </div>
          </>
        );
        const cls = 'flex items-center gap-3 py-2 border-b border-neutral-100 dark:border-neutral-800 last:border-0 hover:bg-neutral-50 dark:hover:bg-neutral-800/40 -mx-2 px-2 rounded transition-colors';
        return item.href ? (
          <Link key={item.id} href={item.href} className={cls}>{content}</Link>
        ) : (
          <div key={item.id} className={cls}>{content}</div>
        );
      })}
    </div>
  );
}

/* ───────────── Facturat / Încasat azi & lună ───────────── */

function isoDate(d: Date): string {
  return d.getFullYear() + '-' + String(d.getMonth() + 1).padStart(2, '0') + '-' + String(d.getDate()).padStart(2, '0');
}

function startOfDay(): Date {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
}

function startOfMonth(): Date {
  const d = new Date();
  return new Date(d.getFullYear(), d.getMonth(), 1);
}

export function InvoicedTodayWidget() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'invoiced-today'],
    queryFn: () => {
      const today = isoDate(new Date());
      return api.get<{ data: any[] }>(
        '/invoices?' + new URLSearchParams({
          limit: '100',
          direction: 'outgoing',
          type: 'factura',
          dateFrom: today,
          dateTo: today,
        }).toString()
      );
    },
    staleTime: 60 * 1000,
  });

  if (isLoading) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const items = ((data?.data || []) as any[]).filter((i) => i.status !== 'cancelled' && i.status !== 'draft');
  const total = items.reduce((s, i) => s + Number(i.total || 0), 0);
  const parts = splitAmount(total);

  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] text-neutral-900 dark:text-neutral-100 my-2"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontSize: '1.875rem' }}
      >
        {parts.main}
        {parts.decimals && (
          <span className="font-normal text-neutral-500" style={{ fontSize: '1rem' }}>{parts.decimals}</span>
        )}
        <span className="ml-1.5 text-[0.6875rem] font-medium text-neutral-400">RON</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        {items.length} {items.length === 1 ? 'factură' : 'facturi'} emise azi
      </div>
    </>
  );
}

export function CollectedTodayWidget() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'collected-today'],
    queryFn: () =>
      api.get<any>('/payments?' + new URLSearchParams({
        limit: '200',
        dateFrom: isoDate(startOfDay()),
      }).toString()).catch(() => ({ data: [] })),
    staleTime: 60 * 1000,
  });

  if (isLoading) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const raw = Array.isArray(data) ? data : (data?.data || []);
  const today = startOfDay();
  const items = (raw as any[]).filter((p) => {
    const d = new Date(p.date || p.paymentDate || p.createdAt);
    return d >= today;
  });
  const total = items.reduce((s, p) => s + Number(p.amount || 0), 0);
  const parts = splitAmount(total);

  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] text-emerald-700 dark:text-emerald-400 my-2"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontSize: '1.875rem' }}
      >
        {parts.main}
        {parts.decimals && (
          <span className="font-normal text-emerald-600/70" style={{ fontSize: '1rem' }}>{parts.decimals}</span>
        )}
        <span className="ml-1.5 text-[0.6875rem] font-medium text-neutral-400">RON</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        {items.length} {items.length === 1 ? 'plată înregistrată' : 'plăți înregistrate'} azi
      </div>
    </>
  );
}

export function CollectedMonthWidget() {
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['dashboard', 'collected-month'],
    queryFn: () =>
      api.get<any>('/payments?' + new URLSearchParams({
        limit: '500',
        dateFrom: isoDate(startOfMonth()),
      }).toString()).catch(() => ({ data: [] })),
    staleTime: 2 * 60 * 1000,
  });

  if (isLoading) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const raw = Array.isArray(data) ? data : (data?.data || []);
  const monthStart = startOfMonth();
  const items = (raw as any[]).filter((p) => {
    const d = new Date(p.date || p.paymentDate || p.createdAt);
    return d >= monthStart;
  });
  const total = items.reduce((s, p) => s + Number(p.amount || 0), 0);
  const parts = splitAmount(total);
  const monthLabel = new Date().toLocaleDateString('ro-RO', { month: 'long' });

  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] text-emerald-700 dark:text-emerald-400 my-2"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontWeight: 600, fontSize: '2.125rem' }}
      >
        {parts.main}
        {parts.decimals && (
          <span className="font-normal text-emerald-600/70" style={{ fontSize: '1.125rem' }}>{parts.decimals}</span>
        )}
        <span className="ml-1.5 text-[0.75rem] font-medium text-neutral-400">RON</span>
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        {items.length} {items.length === 1 ? 'plată' : 'plăți'} în {monthLabel}
      </div>
    </>
  );
}

/**
 * Pending stock decrement — count of invoices issued but not yet
 * stock-decremented. Surfaces the descarcare-facturi page so users notice
 * when the queue grows. Click → navigates to the page.
 */
export function PendingStockDecrementWidget() {
  const { hasModule } = useHasModule('stock');

  const { data, isLoading, isError, refetch } = useQuery<{
    data: any[];
    meta: { total: number };
  }>({
    queryKey: ['pending-stock-decrement-widget'],
    queryFn: () => api.get('/invoices/pending-stock-decrement?limit=1&page=1'),
    staleTime: 60_000,
    enabled: hasModule,
  });

  if (!hasModule) {
    return (
      <WidgetTeaser
        moduleCode="stock"
        hint="Facturi emise pentru care stocul aferent încă nu a fost descărcat din gestiune."
      />
    );
  }
  if (isLoading) return <div className="animate-pulse h-16 bg-neutral-100 dark:bg-neutral-800 rounded" />;
  if (isError) return <WidgetErrorState onRetry={() => refetch()} />;
  const total = data?.meta?.total || 0;
  const hasPending = total > 0;

  return (
    <>
      <div
        className="font-medium leading-none tracking-[-0.03em] my-2"
        style={{ fontFamily: 'var(--font-poppins), sans-serif', fontWeight: 600, fontSize: '2.125rem', color: hasPending ? 'rgb(217, 119, 6)' : 'rgb(15,14,15)' }}
      >
        {total}
      </div>
      <div className="text-xs text-neutral-500 dark:text-neutral-400">
        {hasPending ? (
          <>
            {total === 1 ? 'factură cu stoc nedescărcat' : 'facturi cu stoc nedescărcat'}
            {' · '}
            <Link href="/stocuri/descarcare-facturi" className="text-primary hover:underline font-medium">Vezi listă</Link>
          </>
        ) : (
          <span className="text-emerald-700 dark:text-emerald-400">Stocurile sunt la zi</span>
        )}
      </div>
    </>
  );
}

